import ArticleImage from "../components/ArticleImage";
import { Link, Navigate, useParams } from "react-router-dom";
import SEO from "../components/SEO";
import EditorialArticleHero from "../components/EditorialArticleHero";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import TechArticleAuthorBox from "../components/TechArticleAuthorBox";
import ArticleAd from "../ads/ArticleAd";
import InArticleAd from "../ads/InArticleAd";
import SidebarAd from "../ads/SidebarAd";
import { aiArticles } from "../data/aiArticles";
import {
  aiArticleLocales,
  getAIArticleAlternates,
  getLocalizedAIArticle,
  localizedAIArticles2026,
} from "../data/localizedAIArticles2026";
import {
  certificationArticleSlugs,
  getCertificationArticleAlternates,
  getLocalizedCertificationArticle,
  localizedCertificationArticles2026,
} from "../data/certificationArticles2026";
import { seoConfig } from "../data/seoConfig";


const uiText = {
  en: { home: "Home", index: "AI News", notFound: "AI article not found", notFoundBody: "Open the article index to choose another guide.", openIndex: "Open article index", keyPoints: "Key points", sources: "Sources", structure: "Article structure", related: "Related AI articles", languages: "Read this article in another language", cta: "Follow TechniaHQRobot for researched practical guides" },
  "zh-cn": { home: "首页", index: "人工智能资讯", notFound: "未找到这篇文章", notFoundBody: "请返回人工智能文章目录选择其他指南。", openIndex: "打开文章目录", keyPoints: "核心要点", sources: "资料来源", structure: "文章结构", related: "相关文章", languages: "选择其他语言版本", cta: "关注 TechniaHQRobot，阅读经过核查的实用指南" },
  ja: { home: "ホーム", index: "AIニュース", notFound: "記事が見つかりません", notFoundBody: "AI記事一覧から別のガイドを選んでください。", openIndex: "記事一覧を開く", keyPoints: "要点", sources: "情報源", structure: "記事構成", related: "関連記事", languages: "別の言語で読む", cta: "調査に基づく実用ガイドを TechniaHQRobot で読む" },
  fr: { home: "Accueil", index: "Actualités IA", notFound: "Article introuvable", notFoundBody: "Ouvrez l’index des articles pour choisir un autre guide.", openIndex: "Ouvrir l’index", keyPoints: "Points essentiels", sources: "Sources", structure: "Structure de l’article", related: "Articles associés", languages: "Lire cet article dans une autre langue", cta: "Suivez TechniaHQRobot pour des guides pratiques documentés" },
  de: { home: "Startseite", index: "KI-News", notFound: "Artikel nicht gefunden", notFoundBody: "Öffnen Sie die Artikelübersicht und wählen Sie einen anderen Leitfaden.", openIndex: "Artikelübersicht öffnen", keyPoints: "Wichtige Punkte", sources: "Quellen", structure: "Artikelstruktur", related: "Verwandte Artikel", languages: "Diesen Artikel in einer anderen Sprache lesen", cta: "Folgen Sie TechniaHQRobot für recherchierte Praxisleitfäden" },
  es: { home: "Inicio", index: "Noticias de IA", notFound: "Artículo no encontrado", notFoundBody: "Abre el índice para elegir otra guía.", openIndex: "Abrir índice", keyPoints: "Puntos clave", sources: "Fuentes", structure: "Estructura del artículo", related: "Artículos relacionados", languages: "Leer en otro idioma", cta: "Sigue TechniaHQRobot para consultar guías prácticas documentadas" },
  pt: { home: "Início", index: "Notícias de IA", notFound: "Artigo não encontrado", notFoundBody: "Abra o índice para escolher outro guia.", openIndex: "Abrir índice", keyPoints: "Pontos principais", sources: "Fontes", structure: "Estrutura do artigo", related: "Artigos relacionados", languages: "Ler em outro idioma", cta: "Siga a TechniaHQRobot para guias práticos documentados" },
};

function ResponsiveTable({ table }) {
  if (!table?.headers?.length || !table?.rows?.length) return null;

  return (
    <div className="mt-5 overflow-x-auto rounded-[1.25rem] border border-neutral-200 bg-white" role="region" aria-label={table.caption || "Article data table"} tabIndex="0">
      {table.caption && (
        <p className="border-b border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-black text-neutral-950">{table.caption}</p>
      )}
      <table className="min-w-[760px] w-full border-collapse text-left text-sm">
        <thead className="bg-neutral-950 text-white">
          <tr>
            {table.headers.map((header) => (
              <th key={header} className="px-4 py-3 font-black">{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIndex) => (
            <tr key={`${table.caption || "table"}-${rowIndex}`} className="border-t border-neutral-200">
              {row.map((cell, cellIndex) => (
                <td key={`${rowIndex}-${cellIndex}`} className="px-4 py-3 align-top leading-6 text-neutral-700">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function cleanDisplayText(value = "") {
  return String(value).replace(/: /g, " ").replace(/ \| /g, " ");
}

function parseInlineMarkdown(text = "") {
  const parts = String(text).split(/(\[[^\]]+\]\((?:https?:\/\/|\/(?!\/)|#)[^\s)]+\)|\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    const link = part.match(/^\[([^\]]+)\]\(([^\s)]+)\)$/);
    if (link) {
      return <a key={`link-${index}`} href={link[2]} className="font-semibold text-blue-700 underline underline-offset-2" rel={link[2].startsWith("http") ? "noopener noreferrer" : undefined}>{link[1]}</a>;
    }
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={`${part}-${index}`} className="font-black text-neutral-950">{part.slice(2, -2)}</strong>;
    }
    return <span key={`${part}-${index}`}>{part}</span>;
  });
}

function slugifyHeading(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
}

function extractMarkdownToc(markdown = "") {
  return String(markdown || "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("## "))
    .map((line) => cleanDisplayText(line.replace(/^##\s+/, "")))
    .filter(Boolean)
    .slice(0, 6);
}

function MarkdownTable({ lines, index }) {
  const rows = lines
    .map((line) => line.trim())
    .filter((line) => line.startsWith("|") && line.endsWith("|"))
    .map((line) => line.slice(1, -1).split("|").map((cell) => cell.trim()));

  if (rows.length < 2) return null;
  const [headers, separator, ...bodyRows] = rows;
  const isSeparator = separator.every((cell) => /^:?-{3,}:?$/.test(cell));
  const cleanBody = isSeparator ? bodyRows : rows.slice(1);

  return (
    <div key={`markdown-table-${index}`} className="my-8 overflow-x-auto rounded-[1.25rem] border border-neutral-200 bg-white" role="region" aria-label="Article comparison table" tabIndex="0">
      <table className="min-w-[820px] w-full border-collapse text-left text-sm">
        <thead className="bg-neutral-950 text-white">
          <tr>
            {headers.map((header, headerIndex) => (
              <th key={`${header}-${headerIndex}`} className="px-4 py-3 align-top font-black">{parseInlineMarkdown(header)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cleanBody.map((row, rowIndex) => (
            <tr key={`markdown-row-${rowIndex}`} className="border-t border-neutral-200">
              {row.map((cell, cellIndex) => (
                <td key={`${rowIndex}-${cellIndex}`} className="px-4 py-3 align-top leading-6 text-neutral-700">{parseInlineMarkdown(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MarkdownRenderer({ markdown, skipFirstH1 = false }) {
  const lines = String(markdown || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let index = 0;
  let skippedH1 = false;

  const pushParagraph = (paragraphLines) => {
    const text = paragraphLines.join("\n").trim();
    if (!text) return;
    blocks.push(
      <p key={`p-${blocks.length}`} className="text-[1.02rem] leading-8 text-neutral-700">
        {parseInlineMarkdown(text)}
      </p>
    );
  };

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (trimmed.startsWith("# ")) {
      if (skipFirstH1 && !skippedH1) {
        skippedH1 = true;
        index += 1;
        continue;
      }
      const heading = cleanDisplayText(trimmed.replace(/^#\s+/, ""));
      blocks.push(<h1 id={slugifyHeading(heading)} key={`h1-${blocks.length}`} className="mt-10 text-3xl font-black tracking-tight text-neutral-950 sm:text-4xl">{heading}</h1>);
      index += 1;
      continue;
    }

    if (trimmed.startsWith("## ")) {
      const heading = cleanDisplayText(trimmed.replace(/^##\s+/, ""));
      blocks.push(<h2 id={slugifyHeading(heading)} key={`h2-${blocks.length}`} className="mt-12 border-t border-neutral-200 pt-8 text-2xl font-black tracking-tight text-neutral-950 sm:text-3xl">{heading}</h2>);
      index += 1;
      continue;
    }

    if (trimmed.startsWith("### ")) {
      const heading = cleanDisplayText(trimmed.replace(/^###\s+/, ""));
      blocks.push(<h3 id={slugifyHeading(heading)} key={`h3-${blocks.length}`} className="mt-8 text-xl font-black tracking-tight text-neutral-950 sm:text-2xl">{heading}</h3>);
      index += 1;
      continue;
    }

    if (/^\|.*\|$/.test(trimmed)) {
      const tableLines = [];
      while (index < lines.length && /^\|.*\|$/.test(lines[index].trim())) {
        tableLines.push(lines[index]);
        index += 1;
      }
      blocks.push(<MarkdownTable key={`table-${blocks.length}`} lines={tableLines} index={blocks.length} />);
      continue;
    }

    if (/^[-*•]\s+/.test(trimmed)) {
      const items = [];
      while (index < lines.length && /^[-*•]\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^[-*•]\s+/, ""));
        index += 1;
      }
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="my-5 grid gap-2 pl-0 text-[1.02rem] leading-8 text-neutral-700">
          {items.map((item, itemIndex) => (
            <li key={`${item}-${itemIndex}`} className="flex gap-3">
              <span className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" aria-hidden="true" />
              <span>{parseInlineMarkdown(item)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\d+\.\s+/, ""));
        index += 1;
      }
      blocks.push(
        <ol key={`ol-${blocks.length}`} className="my-5 grid list-decimal gap-2 pl-6 text-[1.02rem] leading-8 text-neutral-700 marker:font-black marker:text-blue-700">
          {items.map((item, itemIndex) => <li key={`${item}-${itemIndex}`}>{parseInlineMarkdown(item)}</li>)}
        </ol>
      );
      continue;
    }

    const paragraphLines = [];
    while (index < lines.length) {
      const current = lines[index];
      const currentTrimmed = current.trim();
      if (!currentTrimmed) break;
      if (/^#{1,3}\s+/.test(currentTrimmed) || /^[-*•]\s+/.test(currentTrimmed) || /^\d+\.\s+/.test(currentTrimmed) || /^\|.*\|$/.test(currentTrimmed)) break;
      paragraphLines.push(current);
      index += 1;
    }
    pushParagraph(paragraphLines);
  }

  return <div className="grid gap-5">{blocks}</div>;
}

function removeRepeatedMarkdownParagraphs(markdown = "", seedTexts = []) {
  const normalize = (value = "") => String(value).replace(/\s+/g, " ").trim().toLowerCase();
  const seen = new Set(seedTexts.map(normalize).filter(Boolean));

  const blocks = String(markdown || "")
    .replace(/\r\n/g, "\n")
    .split(/\n\s*\n/)
    .filter((block) => {
      const trimmed = block.trim();
      if (!trimmed) return false;
      if (/^#{1,6}\s/.test(trimmed) || /^\|/.test(trimmed) || /^[-*•]\s/.test(trimmed) || /^\d+\.\s/.test(trimmed)) return true;
      const normalized = normalize(trimmed);
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
  // A paragraph repeated in the visible summary may leave an empty section.
  // Remove its heading as well so the contents list does not promise a section
  // that has no text. Keep headings that introduce a nested subsection.
  return blocks.filter((block, index) => {
    const heading = block.trim().match(/^(#{1,6})\s+[^\n]+$/);
    if (!heading) return true;
    const next = blocks[index + 1]?.trim();
    if (!next) return false;
    const nextHeading = next.match(/^(#{1,6})\s+/);
    return !nextHeading || nextHeading[1].length > heading[1].length;
  }).join("\n\n");
}

function splitMarkdownForAds(markdown = "", adBreaks = []) {
  const markers = Array.isArray(adBreaks) && adBreaks.length > 0
    ? adBreaks
    : ["## Grok 4.5 vs OpenAI"];
  const parts = [];
  let remaining = String(markdown || "").replace(/\r\n/g, "\n");

  for (const marker of markers) {
    const cleanMarker = String(marker || "").trim();
    if (!cleanMarker) continue;
    const markerWithNewline = `\n${cleanMarker}\n`;
    const markerIndex = remaining.indexOf(markerWithNewline);
    if (markerIndex === -1) continue;
    const before = remaining.slice(0, markerIndex).trim();
    if (before) parts.push(before);
    remaining = remaining.slice(markerIndex + 1);
  }

  if (remaining.trim()) parts.push(remaining.trim());
  return parts.length > 0 ? parts : [markdown];
}

function ArticleNotFound({ locale = "en" }) {
  const labels = uiText[locale] || uiText.en;
  const indexPath = locale === "en" ? "/ai" : ["es", "pt"].includes(locale) ? `/${locale}` : `/${locale}/ai`;
  return (
    <section className="site-shell py-16">
      <SEO
        title={`${labels.notFound} TechniaHQRobot`}
        description={labels.notFoundBody}
        noindex
        lang={aiArticleLocales[locale]?.lang || locale}
      />
      <div className="soft-card p-8">
        <h1 className="text-3xl font-black text-neutral-950">{labels.notFound}</h1>
        <p className="mt-3 text-neutral-600">{labels.notFoundBody}</p>
        <Link to={indexPath} className="btn-primary mt-6">{labels.openIndex}</Link>
      </div>
    </section>
  );
}

function MarkdownAIArticle({ article, breadcrumbs, schemas, locale = "en", articlePool = aiArticles, alternates = [] }) {
  const labels = uiText[locale] || uiText.en;
  const visibleMarkdown = removeRepeatedMarkdownParagraphs(article.markdown, [
    article.heroSubtitle,
    article.description,
    ...(article.keyPoints || []),
  ]);
  const markdownParts = splitMarkdownForAds(visibleMarkdown, article.adBreaks);
  const tocHeadings = extractMarkdownToc(visibleMarkdown);
  const relatedLinks = (article.internalLinks || []).filter((item) => item?.path && item?.label);
  const linkedPaths = new Set(relatedLinks.map((item) => item.path));
  const relatedArticles = articlePool.filter((item) => item.slug !== article.slug && !linkedPaths.has(item.path)).slice(0, 4);

  return (
    <div className="article-page">
      <SEO
        title={article.seoTitle || article.title}
        description={article.description}
        path={article.path}
        type="article"
        image={article.image}
        imageAlt={article.imageAlt}
        schema={schemas}
        lang={article.hreflang || aiArticleLocales[locale]?.lang || locale}
        alternates={alternates}
        publishedTime={article.datePublished}
        modifiedTime={article.dateModified || article.datePublished}
        author="TechniaHQRobot"
        section={article.category}
        tags={article.keywords || []}
      />
      <Breadcrumbs items={breadcrumbs} />
      {alternates.length > 1 && (
        <nav className="site-shell pt-4" aria-label={labels.languages}>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-neutral-500">{labels.languages}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {alternates.filter((item) => item.locale !== "x-default").map((item) => (
              <Link key={item.locale} to={item.href} hrefLang={item.hreflang} className={`rounded-full border px-3 py-2 text-sm font-black transition ${item.locale === locale ? "border-blue-600 bg-blue-600 text-white" : "border-neutral-200 bg-white text-neutral-700 hover:border-blue-300 hover:text-blue-700"}`}>
                {aiArticleLocales[item.locale]?.label || item.hreflang}
              </Link>
            ))}
          </div>
        </nav>
      )}

      <article className="site-shell py-10 sm:py-14">
        <EditorialArticleHero
          article={article}
          title={article.title}
          subtitle={article.heroSubtitle || article.description}
          category={article.category}
          author={article.authorName || article.author || "TechniaHQRobot"}
        />
        <ArticleImage article={article} />

        <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="min-w-0">
            {!article.hideKeyPoints && <section className="rounded-[1.75rem] border border-blue-100 bg-blue-50 p-5 sm:p-6">
              <h2 className="text-sm font-black uppercase tracking-[0.18em] text-blue-700">{labels.keyPoints}</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {(article.keyPoints || []).slice(0, 4).map((point) => (
                  <p key={point} className="rounded-2xl border border-blue-100 bg-white px-4 py-3 text-sm font-bold leading-6 text-blue-950">{point}</p>
                ))}
              </div>
            </section>}

            <ArticleAd placement="top" articleSlug={article.slug} className="my-8" />

            {markdownParts.map((part, partIndex) => (
              <div key={`markdown-part-${partIndex}`}>
                <div className="rounded-[1.75rem] border border-neutral-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.06)] sm:p-8">
                  <MarkdownRenderer markdown={part} skipFirstH1={partIndex === 0} />
                </div>
                {partIndex === 0 && <ArticleAd index={2} totalSections={6} articleSlug={article.slug} className="my-8" />}
                {partIndex === 1 && <ArticleAd index={3} totalSections={6} articleSlug={article.slug} className="my-8" />}
              </div>
            ))}

            <ArticleAd placement="bottom" articleSlug={article.slug} className="my-8" />
            <TechArticleAuthorBox author="@techniahqrobot" />

            {(relatedLinks.length > 0 || relatedArticles.length > 0) && (
              <section className="mt-10 rounded-[1.75rem] border border-neutral-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.06)] sm:p-6">
                <h2 className="text-2xl font-black tracking-tight text-neutral-950">{labels.related}</h2>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {relatedLinks.map((item) => {
                    const isDownload = /\.(?:csv|json|pdf|zip)(?:$|\?)/i.test(item.path);
                    const className = "rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-black leading-6 text-neutral-800 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700";
                    return isDownload ? (
                      <a key={item.path} href={item.path} className={className}>{item.label}</a>
                    ) : (
                      <Link key={item.path} to={item.path} className={className}>{item.label}</Link>
                    );
                  })}
                  {relatedArticles.map((item) => (
                    <Link key={item.path} to={item.path} className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-black leading-6 text-neutral-800 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">{item.title}</Link>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="grid h-fit gap-5 lg:sticky lg:top-24">
            <SidebarAd articleSlug={article.slug} />
            {(article.downloadLinks || []).length > 0 && (
              <div className="soft-card p-5">
                <h2 className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500">Resources</h2>
                <div className="mt-4 grid gap-3 text-sm font-bold leading-6">
                  {article.downloadLinks.map((resource) => (
                    <a key={resource.path} href={resource.path} className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-blue-800 transition hover:border-blue-400">
                      {resource.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
            {(article.sourceLinks || []).length > 0 && (
              <div className="soft-card p-5">
                <h2 className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500">{labels.sources}</h2>
                <div className="mt-4 grid gap-3 text-sm font-bold leading-6">
                  {article.sourceLinks.map((source) => (
                    <a key={source.url} href={source.url} target="_blank" rel="noreferrer" className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-neutral-800 transition hover:border-blue-200 hover:text-blue-700">
                      {source.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
            {tocHeadings.length > 0 && (
              <div className="soft-card p-5">
                <h2 className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500">{labels.structure}</h2>
                <div className="mt-4 grid gap-2 text-sm font-bold">
                  {tocHeadings.map((heading) => (
                    <a key={heading} href={`#${slugifyHeading(heading)}`} className="rounded-2xl bg-neutral-50 px-4 py-3 text-neutral-800 transition hover:bg-blue-50 hover:text-blue-700">{heading}</a>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </article>
      <CTASection title={labels.cta} />
    </div>
  );
}

export default function AIArticlePage({ locale = "en" }) {
  const { slug } = useParams();
  const article = locale === "en"
    ? aiArticles.find((item) => item.slug === slug)
    : getLocalizedCertificationArticle(locale, slug) || getLocalizedAIArticle(locale, slug);
  const labels = uiText[locale] || uiText.en;
  const articlePool = locale === "en"
    ? aiArticles
    : [
        ...localizedCertificationArticles2026.filter((item) => item.locale === locale),
        ...localizedAIArticles2026.filter((item) => item.locale === locale),
      ];

  if (!article) return <ArticleNotFound locale={locale} />;

  if (locale === "en" && article.path?.startsWith("/ai/") && typeof window !== "undefined" && window.location.pathname.startsWith("/ai-news/")) {
    return <Navigate to={article.path} replace />;
  }

  const isPracticalGuide = article.path?.includes("/guides/");
  const localeRoot = locale === "en" ? "/" : `/${locale}`;
  const parentPath = isPracticalGuide
    ? "/guides"
    : ["en", "es", "pt"].includes(locale)
      ? "/ai"
      : `/${locale}/ai`;
  const parentLabel = isPracticalGuide ? "Practical Guides" : locale === "es" ? "Guías de IA en inglés" : locale === "pt" ? "Guias de IA em inglês" : labels.index;
  const breadcrumbs = [
    { label: labels.home, to: localeRoot },
    { label: parentLabel, to: parentPath },
    { label: article.title, to: article.path }
  ];
  const alternates = certificationArticleSlugs.includes(article.slug)
    ? getCertificationArticleAlternates(article.slug)
    : getAIArticleAlternates(article.slug);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: { "@type": "Organization", name: "TechniaHQRobot" },
    publisher: { "@type": "Organization", name: "TechniaHQRobot" },
    mainEntityOfPage: `${seoConfig.baseUrl}${article.path}`,
    inLanguage: article.hreflang || aiArticleLocales[locale]?.lang || locale,
    isPartOf: { "@type": "WebSite", name: "TechniaHQRobot", url: seoConfig.baseUrl },
    ...(locale !== "en" ? { translationOfWork: { "@type": "Article", url: `${seoConfig.baseUrl}/ai/${article.slug}` } } : {})
  };

  // FAQ structured data is intentionally omitted here because this template does not
  // render the article.faq collection as visible page content. Keep schema aligned
  // with what readers can actually see.
  const schemas = [buildBreadcrumbSchema(breadcrumbs), schema].filter(Boolean);

  if (article.markdown) {
    return <MarkdownAIArticle article={article} breadcrumbs={breadcrumbs} schemas={schemas} locale={locale} articlePool={articlePool} alternates={alternates} />;
  }

  return (
    <div className="article-page">
      <SEO
        title={article.seoTitle || article.title}
        description={article.description}
        path={article.path}
        type="article"
        image={article.image}
        imageAlt={article.imageAlt}
        schema={schemas}
        lang={article.hreflang || aiArticleLocales[locale]?.lang || locale}
        alternates={alternates}
        publishedTime={article.datePublished}
        modifiedTime={article.dateModified || article.datePublished}
        author="TechniaHQRobot"
        section={article.category}
        tags={article.keywords || []}
      />
      <Breadcrumbs items={breadcrumbs} />
      <section className="site-shell py-8 sm:py-10 lg:py-12">
        <EditorialArticleHero
          article={article}
          title={article.title}
          subtitle={article.heroSubtitle || article.description}
          category={article.category}
          author={article.authorName || article.author || "TechniaHQRobot"}
        />
        <ArticleImage article={article} />
      </section>

      <article className="site-shell py-10 sm:py-14">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="soft-card p-6 sm:p-8">
            <p className="rounded-[1.5rem] border border-blue-100 bg-blue-50 px-5 py-4 text-base leading-8 text-blue-950">{article.trendReason}</p>

            <ArticleAd placement="top" articleSlug={article.slug} className="mt-8" />

            <div className="mt-8 grid gap-9">
              {article.sections.map((section, index) => (
                <div key={section.heading}>
                  <section>
                    <h2 className="text-2xl font-black tracking-tight text-neutral-950">{section.heading}</h2>
                    <div className="mt-4 grid gap-4 text-base leading-8 text-neutral-700">
                      {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                    </div>
                    <ResponsiveTable table={section.table} />
                  </section>
                  <InArticleAd index={index + 1} totalSections={article.sections.length} articleSlug={article.slug} />
                </div>
              ))}
            </div>
            <ArticleAd placement="bottom" articleSlug={article.slug} className="mt-10" />

            <TechArticleAuthorBox author="@techniahqrobot" />
          </div>

          <aside className="grid h-fit gap-5 lg:sticky lg:top-24">
            <SidebarAd articleSlug={article.slug} />

            {article.sourceLinks.length > 0 && (
              <div className="soft-card p-5">
                <h2 className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500">Sources</h2>
                <div className="mt-4 grid gap-3 text-sm font-bold leading-6">
                  {article.sourceLinks.map((source) => (
                    <a key={source.url} href={source.url} target="_blank" rel="noreferrer" className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-neutral-800 transition hover:border-blue-200 hover:text-blue-700">
                      {source.label}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="soft-card p-5">
              <h2 className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500">More guides and AI topics</h2>
              <div className="mt-4 grid gap-2 text-sm font-bold">
                {aiArticles.filter((item) => item.slug !== article.slug).slice(0, 5).map((item) => (
                  <Link key={item.slug} to={item.path} className="rounded-2xl bg-neutral-50 px-4 py-3 text-neutral-800 transition hover:bg-blue-50 hover:text-blue-700">
                    {item.title}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </article>
      <CTASection title="Follow TechniaHQRobot for researched practical guides" />
    </div>
  );
}
